import { create } from "zustand";
import {
  guildApi,
  type GuildApplicationView,
  type GuildBattleLogEntry,
  type GuildBattleLiveView,
  type GuildMemberView,
  type GuildOverview,
  type GuildSearchResult,
  type GuildShopItemView,
  type NpcFillerView,
  type PlatinumVoteView,
} from "../api/guild";
import { useGameStore } from "./useGameStore";

interface GuildState {
  overview: GuildOverview | null;
  searchResults: GuildSearchResult[];
  members: GuildMemberView[];
  applications: GuildApplicationView[];
  shopItems: GuildShopItemView[];
  myContribution: number;
  npcFillers: NpcFillerView[];
  canPurchaseNpc: boolean;
  npcBattleWeekKey: string | null;
  treasuryGold: number;
  battleLogs: GuildBattleLogEntry[];
  battleLive: GuildBattleLiveView | null;
  battleLivePhaseKey: string | null;
  dismissedReplayMatchIds: string[];
  platinumVotes: PlatinumVoteView[];
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  message: string | null;
  refresh: () => Promise<void>;
  search: (q: string) => Promise<void>;
  createGuild: (name: string) => Promise<boolean>;
  apply: (guildId: number, message?: string) => Promise<boolean>;
  leave: () => Promise<boolean>;
  loadMembers: () => Promise<void>;
  loadApplications: () => Promise<void>;
  acceptApplication: (id: number) => Promise<boolean>;
  rejectApplication: (id: number) => Promise<boolean>;
  donateGold: (amount: number) => Promise<boolean>;
  setBattleTarget: (palace: string) => Promise<boolean>;
  setRoster: (slots: { slot: number; user_id?: number | null; npc_tier?: number | null }[]) => Promise<boolean>;
  loadShop: () => Promise<void>;
  buyItem: (itemId: string) => Promise<boolean>;
  loadNpcFillers: () => Promise<void>;
  buyNpc: (slot: number, tier: number) => Promise<boolean>;
  promoteMember: (targetUserId: number, role: string) => Promise<boolean>;
  loadBattleLogs: () => Promise<void>;
  loadBattleLive: () => Promise<void>;
  dismissReplayMatch: (matchId: string) => void;
  enterBattle: () => Promise<boolean>;
  claimPlatinumSeat: (seatIndex: number) => Promise<boolean>;
  ackBattleReplay: (matchId: string) => Promise<void>;
  completePalaceWatch: () => Promise<void>;
  loadPlatinumVotes: () => Promise<void>;
  createPlatinumVote: (title: string, description: string) => Promise<boolean>;
  castPlatinumVote: (voteId: number, choice: "yes" | "no" | "abstain") => Promise<boolean>;
  sendPresence: () => Promise<void>;
  clearMessage: () => void;
}

export const useGuildStore = create<GuildState>((set, get) => ({
  overview: null,
  searchResults: [],
  members: [],
  applications: [],
  shopItems: [],
  myContribution: 0,
  npcFillers: [],
  canPurchaseNpc: false,
  npcBattleWeekKey: null,
  treasuryGold: 0,
  battleLogs: [],
  battleLive: null,
  battleLivePhaseKey: null,
  dismissedReplayMatchIds: [],
  platinumVotes: [],
  loading: false,
  actionLoading: false,
  error: null,
  message: null,

  refresh: async () => {
    set({ loading: true, error: null });
    try {
      const overview = await guildApi.overview();
      set({ overview, loading: false });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Failed to load guild" });
    }
  },

  search: async (q) => {
    try {
      const res = await guildApi.search(q);
      set({ searchResults: res.guilds });
    } catch {
      set({ searchResults: [] });
    }
  },

  createGuild: async (name) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await guildApi.create(name);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Create failed" });
      return false;
    }
  },

  apply: async (guildId, message) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await guildApi.apply(guildId, message);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Apply failed" });
      return false;
    }
  },

  leave: async () => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await guildApi.leave();
      set({
        overview: res.overview,
        members: [],
        applications: [],
        actionLoading: false,
        message: res.message,
      });
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Leave failed" });
      return false;
    }
  },

  loadMembers: async () => {
    const guildId = get().overview?.my_guild?.id;
    if (!guildId) return;
    try {
      const res = await guildApi.members(guildId);
      set({ members: res.members });
    } catch {
      set({ members: [] });
    }
  },

  loadApplications: async () => {
    try {
      const res = await guildApi.applications();
      set({ applications: res.applications });
    } catch {
      set({ applications: [] });
    }
  },

  acceptApplication: async (id) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.acceptApplication(id);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      await get().loadApplications();
      await get().loadMembers();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Accept failed" });
      return false;
    }
  },

  rejectApplication: async (id) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.rejectApplication(id);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      await get().loadApplications();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Reject failed" });
      return false;
    }
  },

  donateGold: async (amount) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.donateGold(amount);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      await useGameStore.getState().refreshBalance();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Donate failed" });
      return false;
    }
  },

  setBattleTarget: async (palace) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.setBattleTarget(palace);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Failed to set target" });
      return false;
    }
  },

  setRoster: async (slots) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.setRoster(slots);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Roster update failed" });
      return false;
    }
  },

  loadShop: async () => {
    try {
      const res = await guildApi.shop();
      set({ shopItems: res.items, myContribution: res.my_contribution });
    } catch {
      set({ shopItems: [], myContribution: 0 });
    }
  },

  buyItem: async (itemId) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.buyShopItem(itemId);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      await get().loadShop();
      await useGameStore.getState().refreshBalance();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Purchase failed" });
      return false;
    }
  },

  loadNpcFillers: async () => {
    try {
      const res = await guildApi.npcFillers();
      set({
        npcFillers: res.fillers,
        treasuryGold: res.gold_treasury,
        canPurchaseNpc: res.can_purchase,
        npcBattleWeekKey: res.battle_week_key ?? null,
      });
    } catch {
      set({ npcFillers: [], treasuryGold: 0, canPurchaseNpc: false, npcBattleWeekKey: null });
    }
  },

  buyNpc: async (slot, tier) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.buyNpc(slot, tier);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      await get().loadNpcFillers();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "NPC purchase failed" });
      return false;
    }
  },

  promoteMember: async (targetUserId, role) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.promoteMember(targetUserId, role);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      await get().loadMembers();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Promote failed" });
      return false;
    }
  },

  loadBattleLogs: async () => {
    try {
      const res = await guildApi.battleLogs();
      set({ battleLogs: res.battles });
    } catch {
      set({ battleLogs: [] });
    }
  },

  loadBattleLive: async () => {
    try {
      const prev = get().battleLive;
      const res = await guildApi.battleLive();
      const live = res.live;
      set((s) => {
        const phaseKey =
          live?.week_key && live?.phase ? `${live.week_key}-${live.phase}` : s.battleLivePhaseKey;
        const prevKey = s.battleLivePhaseKey;
        const phaseChanged =
          phaseKey != null && prevKey != null && phaseKey !== prevKey;
        const prevMatchId = s.battleLive?.current_match?.id;
        const matchChanged =
          live?.current_match?.id != null &&
          prevMatchId != null &&
          live.current_match.id !== prevMatchId;
        const clearDismissed = phaseChanged || matchChanged;
        return {
          battleLive: live,
          battleLivePhaseKey: phaseKey ?? prevKey,
          dismissedReplayMatchIds: clearDismissed ? [] : s.dismissedReplayMatchIds,
        };
      });
      const statusChanged = (prev?.status ?? null) !== (live?.status ?? null);
      const phaseChanged =
        (prev?.phase ?? null) !== (live?.phase ?? null) ||
        (prev?.active ?? null) !== (live?.active ?? null);
      if (statusChanged || phaseChanged) {
        void get().refresh();
      }
    } catch {
      set({ battleLive: null });
    }
  },

  dismissReplayMatch: (matchId) => {
    set((s) => ({
      dismissedReplayMatchIds: s.dismissedReplayMatchIds.includes(matchId)
        ? s.dismissedReplayMatchIds
        : [...s.dismissedReplayMatchIds, matchId],
    }));
  },

  enterBattle: async () => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.enterBattle();
      set({ actionLoading: false, message: res.message });
      await get().loadBattleLive();
      await get().refresh();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Enter battle failed" });
      return false;
    }
  },

  claimPlatinumSeat: async (seatIndex) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.claimPlatinumSeat(seatIndex);
      set({ actionLoading: false, message: res.message });
      await get().loadBattleLive();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Seat claim failed" });
      return false;
    }
  },

  ackBattleReplay: async (matchId) => {
    try {
      await guildApi.ackBattleReplay(matchId);
      get().dismissReplayMatch(matchId);
      await get().loadBattleLive();
      await get().refresh();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Replay ack failed" });
    }
  },

  completePalaceWatch: async () => {
    try {
      await guildApi.completePalaceWatch();
      await get().loadBattleLive();
      await get().refresh();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Watch complete failed" });
    }
  },

  loadPlatinumVotes: async () => {
    try {
      const res = await guildApi.platinumVotes();
      set({ platinumVotes: res.votes });
    } catch {
      set({ platinumVotes: [] });
    }
  },

  createPlatinumVote: async (title, description) => {
    set({ actionLoading: true, error: null });
    try {
      await guildApi.createPlatinumVote(title, description);
      set({ actionLoading: false, message: "Vote created" });
      await get().loadPlatinumVotes();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Create vote failed" });
      return false;
    }
  },

  castPlatinumVote: async (voteId, choice) => {
    set({ actionLoading: true, error: null });
    try {
      const res = await guildApi.castPlatinumVote(voteId, choice);
      set({ overview: res.overview, actionLoading: false, message: res.message });
      await get().loadPlatinumVotes();
      return true;
    } catch (e) {
      set({ actionLoading: false, error: e instanceof Error ? e.message : "Vote failed" });
      return false;
    }
  },

  sendPresence: async () => {
    try {
      await guildApi.presence();
    } catch {
      /* ignore */
    }
  },

  clearMessage: () => set({ message: null, error: null }),
}));

export function palaceLabel(palace: string): string {
  if (palace === "none") return "No palace";
  return palace.charAt(0).toUpperCase() + palace.slice(1);
}

export function formatBattleCountdown(iso: string, totalSeconds?: number): string {
  const diff =
    totalSeconds != null
      ? Math.max(0, totalSeconds)
      : Math.max(0, new Date(iso).getTime() - Date.now());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (days > 0) return `${days}d ${hours}h`;
  if (diff < 600000) return `${mins}:${String(secs).padStart(2, "0")}`;
  if (diff < 3600000) return `${mins}m ${secs}s`;
  return `${hours}h ${mins}m`;
}

export function formatSecondsRemaining(seconds: number): string {
  const diff = Math.max(0, seconds) * 1000;
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (diff >= 3600000) return `${hours}h ${mins}m`;
  if (diff < 600000) return `${mins}:${String(secs).padStart(2, "0")}`;
  return `${mins}m ${secs}s`;
}

export function roleToApi(role: string): string {
  return role.toLowerCase();
}
